/**
 * Chat State Persistence Utility
 * Industry-standard implementation with:
 * - Per-user storage keys
 * - Safe-save to prevent accidental data loss
 * - Automatic migration from legacy global key
 * - Cross-tab synchronization support
 */

export interface PersistedChatState {
  conversationId: string;
  isMinimized: boolean;
  isExpanded: boolean;
  timestamp: number;
  order: number;
}

interface StoredChatData {
  version: number;
  userId: string;
  timestamp: number;
  chats: PersistedChatState[];
}

const LEGACY_STORAGE_KEY = 'floating_chat_state';
const STORAGE_KEY_PREFIX = 'floating_chat_state';
const EXPIRY_DAYS = 7;
const VERSION = 1;

/**
 * Get user-specific storage key
 */
function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

/**
 * Read stored data for a user
 */
function readStored(userId: string): StoredChatData | null {
  try {
    const key = getStorageKey(userId);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const data: StoredChatData = JSON.parse(stored);
    
    // Validate version
    if (data.version !== VERSION) {
      console.warn('[ChatPersistence] Version mismatch, clearing old state');
      localStorage.removeItem(key);
      return null;
    }
    
    // Validate user
    if (data.userId !== userId) {
      console.warn('[ChatPersistence] User mismatch in stored data');
      return null;
    }
    
    // Check expiry
    const age = Date.now() - data.timestamp;
    const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (age > expiryMs) {
      console.warn('[ChatPersistence] Stored state expired, clearing');
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {
    console.warn('[ChatPersistence] Failed to read stored data:', error);
    return null;
  }
}

/**
 * Migrate from legacy global key to per-user key
 */
function migrateLegacyData(userId: string): StoredChatData | null {
  try {
    const legacyStored = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacyStored) return null;
    
    const legacyData: StoredChatData = JSON.parse(legacyStored);
    
    // Only migrate if it belongs to this user
    if (legacyData.userId !== userId) {
      return null;
    }
    
    // Check if not expired
    const age = Date.now() - legacyData.timestamp;
    const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (age > expiryMs) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return null;
    }
    
    console.log('[ChatPersistence] Migrating legacy data for user:', userId);
    
    // Write to new per-user key
    const newKey = getStorageKey(userId);
    localStorage.setItem(newKey, JSON.stringify(legacyData));
    
    // Remove legacy key
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    
    console.log('[ChatPersistence] Migration complete');
    return legacyData;
  } catch (error) {
    console.warn('[ChatPersistence] Failed to migrate legacy data:', error);
    return null;
  }
}

/**
 * Safe-save chat states to localStorage
 * Prevents accidental overwrite of existing state with empty state
 */
export function saveChatStateSafe(
  userId: string,
  chatStates: Map<string, { isMinimized: boolean; isExpanded: boolean }>,
  order: string[],
  options?: { allowEmpty?: boolean }
): void {
  try {
    const chats: PersistedChatState[] = order.map((conversationId, index) => {
      const state = chatStates.get(conversationId);
      return {
        conversationId,
        isMinimized: state?.isMinimized ?? false,
        isExpanded: state?.isExpanded ?? false,
        timestamp: Date.now(),
        order: index,
      };
    });

    // Read existing data
    const existingData = readStored(userId);
    
    // Prevent accidental empty save unless explicitly allowed
    if (chats.length === 0 && 
        existingData && 
        existingData.chats.length > 0 && 
        !options?.allowEmpty) {
      console.log('[ChatPersistence] Skipping empty save to preserve existing state', {
        existingChatCount: existingData.chats.length,
        key: getStorageKey(userId)
      });
      return;
    }

    const data: StoredChatData = {
      version: VERSION,
      userId,
      timestamp: Date.now(),
      chats,
    };

    const key = getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('[ChatPersistence] Failed to save chat state:', error);
  }
}


/**
 * Load chat states from localStorage with automatic migration
 */
export function loadChatState(userId: string): PersistedChatState[] | null {
  try {
    // Try new per-user key first
    let data = readStored(userId);
    
    // If not found, try migrating from legacy key
    if (!data) {
      data = migrateLegacyData(userId);
    }
    
    if (!data) return null;

    // Sort by order
    return data.chats.sort((a, b) => a.order - b.order);
  } catch (error) {
    console.warn('[ChatPersistence] Failed to load chat state:', error);
    return null;
  }
}

/**
 * Clear chat state for a specific user
 */
export function clearChatState(userId: string): void {
  try {
    const key = getStorageKey(userId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('[ChatPersistence] Failed to clear chat state:', error);
  }
}


/**
 * Filter persisted states by valid conversation IDs
 */
export function filterValidChatStates(
  persistedStates: PersistedChatState[],
  validConversationIds: Set<string>
): PersistedChatState[] {
  const filtered = persistedStates.filter((state) => validConversationIds.has(state.conversationId));
  
  if (filtered.length < persistedStates.length) {
    console.log('[ChatPersistence] Filtered out invalid conversations:', {
      original: persistedStates.length,
      filtered: filtered.length,
      removed: persistedStates.length - filtered.length
    });
  }
  
  return filtered;
}
